/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/popup.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Rendering } from 'components/rendering'
import { Highlighter } from 'components/highlighter'
import { SmallMultiples } from 'components/smallmultiples'
import { Methodology } from 'components/methodology'
import { Link } from '@ta-interaktiv/contentbricks'
import { Tooltip } from 'react-tippy'
import 'react-tippy/dist/tippy.css'
import renderHTML from 'react-render-html'
import AnchorLink from 'react-anchor-link-smooth-scroll'

const ARTICLE_ID = '851886647816'

const optimisticText =
  '<p>Das sehr optimistische Szenario <span class="ui green label">SSP1-1.9</span> beschreibt eine Welt, in der die globalen CO<sub>2</sub>-Emissionen bis etwa 2050 auf netto null gesenkt werden. Damit wird das Ziel des Pariser Abkommens erreicht, die vom Menschen verursachte globale Erwärmung auf 1,5 Grad Celsius über den vorindustriellen Temperaturen zu begrenzen.</p>'

const pessimisticText =
  '<p>Das pessimistische Szenario <span class="ui red label">SSP5-8.5</span> zeigt eine Zukunft mit Wirtschaftswachstum, das durch die Verbrennung von Öl, Gas und Kohle angetrieben wird, mit einer prognostizierten weltweiten Erwärmung von 4,4 Grad Celsius bis zum Ende des Jahrhunderts.'

const labelText = ['mit Klimaschutz', 'ohne Klimaschutz']

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      isMobile: window.innerWidth < 600
    }
    this.updateDimensions = this.updateDimensions.bind(this)
  }

  updateDimensions() {
    // console.log('updateDimensions')
    this.setState({ isMobile: window.innerWidth < 600 })
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)
  }

  render() {
    return (
      <div className='index inverted'>
        <div className='awards'>
          <div className='wrapper'>
            <div>
              <a href='https://swisspressaward.ch/en/winners-swiss-press-award-2023/'>
                <img
                  className='ui image'
                  src={require('../../images/logo-swiss-press-award.png')}
                />
              </a>
            </div>
            <div>
              <div>
                <a href='https://ejc.net/news/announcing-the-five-winners-of-the-2023-climate-journalism-award'>
                  <img
                    className='ui image'
                    src={require('../../images/logo-climate-journalism-award.png')}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
        <Masthead inverted articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>So haben Sie den Gletscherschwund noch nie gesehen</h1>
              <h2>
                Simulationen der ETH Zürich und der Universität Freiburg zeigen,
                wie fünf Schweizer Gletscher verschwinden und was mit
                Klimaschutz noch zu retten ist.
              </h2>
            </div>
          </div>
        </header>

        <div className='ui vertical segment' style={{ padding: '0 0 2em' }}>
          <div className='ui container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Matthias Huss, Enrico Mattea, Mathias Lutz, Marc Brupbacher
              </div>
              <div className='ta updated'>Aktualisiert am 6. Juli 2022</div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title='Rhonegletscher'
            canton='VS'
            scenario={labelText}
            showHint
            mp4={require('../../videos/rhone-119-1280-hevc.mp4')}
            webm={require('../../videos/rhone-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/rhone-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/rhone-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/rhone-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/rhone-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/rhone-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/rhone-585-1920-vp9.webm')}
            posterImage={require('../../images/rhone-1280.png')}
            posterImageDesktop={require('../../images/rhone-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Im Jahr 1884 wurden auf dem Rhonegletscher die ersten
              langfristigen Messungen weltweit zur Bestimmung der
              Gletscherschmelze durchgeführt. Der Gletscher ist touristisch gut
              erschlossen und bildet die Quelle der Rhone, eines der grössten
              Flüsse der Schweiz. Etwa im Jahr 2007 bildete sich am Ende des
              Gletschers ein See, der seither jährlich schnell anwächst.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    mit Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/rhone-2100-ssp119.png')}
                  overlayImage={require('../../images/rhone-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    ohne Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/rhone-2100-ssp585.png')}
                  overlayImage={require('../../images/rhone-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Zwischen 2030 und 2040 dürfte die Zunge des Rhonegletschers den
                Kontakt mit dem See verlieren, wie Berechnungen der ETH Zürich
                zeigen (
                <AnchorLink href='#methodik' className='nav-link'>
                  siehe Methodik
                </AnchorLink>
                ). Bis dann wird die heute noch häufig besuchte, künstliche
                Gletschergrotte, deren Bestehen mit weissem Vlies verlängert
                wird, wohl nicht mehr betrieben werden können. In einem Szenario
                mit starkem Temperaturanstieg verschwinden bis ins Jahr 2050
                sämtliche kleinen Gletscher an den Flanken um den
                Rhonegletscher, und dessen Zunge ist von der heutigen Eisgrotte
                aus nicht mehr sichtbar. Dafür könnten mehrere Gletscherseen
                weiter oben die Landschaft ohne Eis prägen. Nach 2080 kann nicht
                einmal mehr der Eisfleck unter dem Dammastock der Sommerhitze
                widerstehen. Im Fall eines starken Klimaschutzes hingegen sehen
                wir sogar 2100 noch einen intakten Gletscher. Dennoch gehen auch
                in diesem Fall rund zwei Drittel des aktuellen Volumens
                verloren.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment dark'>
          <Rendering
            title='Vadret da Morteratsch'
            canton='GR'
            scenario={labelText}
            mp4={require('../../videos/morteratsch-119-1280-hevc.mp4')}
            webm={require('../../videos/morteratsch-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/morteratsch-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/morteratsch-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/morteratsch-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/morteratsch-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/morteratsch-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/morteratsch-585-1920-vp9.webm')}
            posterImage={require('../../images/morteratsch-1280.png')}
            posterImageDesktop={require('../../images/morteratsch-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Der Vadret da Morteratsch im Engadin ist wohl der einzige
              Gletscher der Schweiz, dessen Zunge ab Bahnstation quasi mit dem
              Rollstuhl erreichbar war. Mit dem kürzlichen Rückzug des Eises hat
              sich dies nun aber geändert. Die 3 km lange Wanderung durch ein
              immer grüner werdendes Tal, entlang Jahrestafeln, die den Rückgang
              des Eises dokumentieren, ist eindrücklich.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    mit Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/morteratsch-2100-ssp119.png')}
                  overlayImage={require('../../images/morteratsch-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    ohne Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/morteratsch-2100-ssp585.png')}
                  overlayImage={require('../../images/morteratsch-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Die erste einschneidende Veränderung im Gletschersystem hat sich
                schon vor wenigen Jahren abgespielt: Der grösste Zufluss, der
                Vadret Pers, hat sich vom Hauptstrom abgetrennt und zieht sich
                schnell in ein Seitental zurück. Obwohl in den nächsten
                Jahrzehnten nach wie vor viel Eis durch das Spaltenlabyrinth des
                «Buuch» vom Piz Bernina zur Gletscherzunge fliesst, wird diese
                schnell dünner und verliert schliesslich – in jedem Szenario –
                den Zusammenhang mit dem Nährgebiet. Während sich der Gletscher
                mit von Schutt bedecktem Toteis auf der Zunge nach 2080 im
                günstigen Fall stabilisieren kann, geht ohne Klimaschutz fast
                alles Eis verloren. Auch im Tal des Morteratschgletschers
                könnten sich in Zukunft pittoreske Seen bilden.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title='Grosser Aletschgletscher'
            canton='VS'
            scenario={labelText}
            mp4={require('../../videos/aletsch-119-1280-hevc.mp4')}
            webm={require('../../videos/aletsch-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/aletsch-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/aletsch-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/aletsch-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/aletsch-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/aletsch-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/aletsch-585-1920-vp9.webm')}
            posterImage={require('../../images/aletsch-1280.png')}
            posterImageDesktop={require('../../images/aletsch-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Mit einer Fläche von fast 80 km<sup>2</sup> ist der Grosse
              Aletschgletscher mit Abstand der grösste Gletscher der Alpen.
              Seine 14 km lange Gletscherzunge mit den zwei Mittelmoränen ist
              weltbekannt. Im Aletschgletscher allein sind rund ein Fünftel des
              Eisvolumens der Schweiz gespeichert, und mit rund 800 Metern ist
              das Eis nirgends dicker als am Konkordiaplatz, wo drei grosse
              Gletscher zusammenfliessen.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    mit Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/aletsch-2100-ssp119.png')}
                  overlayImage={require('../../images/aletsch-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    ohne Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/aletsch-2100-ssp585.png')}
                  overlayImage={require('../../images/aletsch-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Aufgrund seiner Grösse könnte man annehmen, dass der
                Aletschgletscher der Klimaänderung am besten trotzt. Das ist
                aber nur bedingt der Fall: Der Gletscher ist viel zu gross für
                das aktuelle Klima und wird sich auch im Fall eines starken
                Klimaschutzes massiv zurückziehen und bis 2100 über die Hälfte
                seines Volumens verlieren. Die geschwungene Gletscherzunge wird
                schnell kürzer und dünner. Bis 2050 geschieht dies fast
                unabhängig von der Klimaentwicklung. Ab etwa 2040 dürfte ein
                erster von vielen Seen im tief eingeschnittenen Tal unter dem
                Aletschgletscher zum Vorschein kommen. Die neuen Seen werden
                fortan die Landschaft prägen. Wenn die Menschheit die Klimakrise
                in den Griff bekommt, kann sich das Gletscherende bis ins Jahr
                2100 stabilisieren, allerdings erst rund 10 km talaufwärts von
                der aktuellen Position. Ohne Klimaschutz hingegen könnte sich um
                etwa 2080 am Konkordiaplatz ein riesiger Gletschersee bilden. In
                diesem Extremszenario bleibt sogar am Jungfraujoch kein Eis mehr
                übrig.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment dark'>
          <Rendering
            title='Gornergletscher'
            canton='VS'
            scenario={labelText}
            mp4={require('../../videos/gorner-119-1280-hevc.mp4')}
            webm={require('../../videos/gorner-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/gorner-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/gorner-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/gorner-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/gorner-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/gorner-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/gorner-585-1920-vp9.webm')}
            posterImage={require('../../images/gorner-1280.png')}
            posterImageDesktop={require('../../images/gorner-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Der Gornergletscher hat seinen Ursprung in den höchsten Gipfeln
              der Schweiz und ist der zweitgrösste Gletscher der Alpen. Der
              Gletscher setzt sich aus verschiedenen Zuflüssen zusammen, die den
              Zusammenhang mit dem Hauptstrom in den letzten Jahren teils schon
              verloren haben.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    mit Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/gorner-2100-ssp119.png')}
                  overlayImage={require('../../images/gorner-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    ohne Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/gorner-2100-ssp585.png')}
                  overlayImage={require('../../images/gorner-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Die Unterschiede zwischen moderatem und starkem Anstieg der
                Lufttemperaturen ist beim Gornergletscher besonders deutlich.
                Aufgrund seiner grossen Höhenerstreckung kann sich der Gletscher
                im Fall eines starken Klimaschutzes gegen Ende des Jahrhunderts
                stabilisieren. Allerdings bildet sich auch in diesem Fall eine
                Toteismasse unter der Monte-Rosa-Hütte, wo das Eis heute noch
                über 400 Meter dick ist. Schon in den nächsten Jahren dürfte das
                Eis Platz machen für einen grossen See vor der Gletscherzunge.
                Im Zusammenspiel mit einer künstlichen Staumauer könnte dieser
                in Zukunft eine wichtige Rolle in der Energiestrategie 2050 des
                Bundes spielen, wie der{' '}
                <Link href='822857521439' hideIcon>
                  «Runde Tisch Wasserkraft»
                </Link>{' '}
                im Januar 2022 aufgezeigt hat. Allerdings wird das Seebecken
                gemäss den Modellrechnungen nur im pessimistischen Szenario vor
                2050 eisfrei, während es im besten Fall bis zum Ende des
                Jahrhunderts teilweise mit Gletschereis gefüllt bleibt. Am Monte
                Rosa verbleibt auch bei extremer Klimaveränderung im Jahr 2100
                noch etwas Eis übrig.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title='Silvretta'
            canton='GR'
            scenario={labelText}
            mp4={require('../../videos/silvretta-119-1280-hevc.mp4')}
            webm={require('../../videos/silvretta-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/silvretta-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/silvretta-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/silvretta-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/silvretta-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/silvretta-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/silvretta-585-1920-vp9.webm')}
            posterImage={require('../../images/silvretta-1280.png')}
            posterImageDesktop={require('../../images/silvretta-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Mit weniger als 3 km<sup>2</sup> ist der Silvrettagletscher der
              Zwerg unter den fünf ausgewählten Gletschern. Dennoch ist er für
              die Schweizer Gletscherbeobachtung sehr wichtig, da dort seit über
              100 Jahren kontinuierlich detaillierte Messungen der
              Schneeanlagerung und Schmelze durchgeführt werden.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    mit Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/silvretta-2100-ssp119.png')}
                  overlayImage={require('../../images/silvretta-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    ohne Klimaschutz
                  </div>
                </Tooltip>
                <h3>Jahr 2100</h3>
                <Highlighter
                  baseImage={require('../../images/silvretta-2100-ssp585.png')}
                  overlayImage={require('../../images/silvretta-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Unabhängig vom Klimaszenario ist ein starker Rückgang des Eises
                in den nächsten Jahrzehnten zu beobachten. Der Gletscher ist
                schon heute relativ dünn, sodass eine Beschleunigung der
                Schmelze schnell zur Freilegung des Untergrunds führt. Im Fall
                einer moderaten Erwärmung mit starkem Klimaschutz verliert der
                Gletscher ab 2050 nur noch langsam an Dicke, da er sich ohne die
                tiefliegenden Bereiche mit starker Schmelze einem Gleichgewicht
                annähern kann. Die Glaziologen könnten die wertvolle Messreihe
                also noch bis Ende des 21. Jahrhunderts weiterführen. Kleine
                Gletscher sind zäher, als man denkt: Wenn wir die Klimakrise in
                den Griff bekommen, könnten viele überleben. Ganz anders sieht
                es ohne Klimaschutz aus: Die sogenannte Gleichgewichtslinie,
                also die Höhe, auf welcher Schnee den Sommer überleben kann,
                steigt in kurzer Zeit über den höchsten Punkt des Gletschers an.
                Damit bleibt ihm keine Chance, sich zu stabilisieren, und bis
                rund 2080 dürfte der letzte Eisrest verschwunden sein. Zurück
                bleibt eine Geröllwüste.
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical segment dark multiple'>
          <div className='ui text container' style={{ margin: '1em 0 2em' }}>
            <div className='ta text'>
              Die Entwicklung des Eisvolumens wurde durch das
              ETH-Berechnungsmodell für jeden Schweizer Gletscher und jedes
              Klimaszenario bestimmt. Ein Vergleich der 38 grössten Gletscher
              zeigt Gemeinsamkeiten, aber auch Unterschiede in der Entwicklung:
            </div>
          </div>
          <div className='ui container' style={{ paddingTop: '1em' }}>
            <div className='ta text'>
              <h4 className='left'>
                Volumen der grössten Schweizer Gletscher bis ins Jahr 2100
              </h4>
              <div className='Key'>
                <div className='entry'>
                  <div className='swatch'>
                    <svg width='12' height='12'>
                      <line
                        x2='12'
                        y1='12'
                        y2='0'
                        stroke='#2ca29f'
                        strokeWidth='4'
                      />
                    </svg>
                  </div>
                  <div className='label'>{labelText[0]}</div>
                </div>
                <div className='entry'>
                  <div className='swatch'>
                    <svg width='12' height='12'>
                      <line
                        x2='12'
                        y1='12'
                        y2='0'
                        stroke='#d3343f'
                        strokeWidth='4'
                      />
                    </svg>
                  </div>
                  <div className='label'>{labelText[1]}</div>
                </div>
              </div>
              <SmallMultiples isMobile={this.state.isMobile} />
            </div>
          </div>
        </div>

        <div className='ui vertical segment dark'>
          <div className='ui text container'>
            <div className='ta text'>
              Mit Ausnahme ganz weniger Gletscher, die ihren Ursprung auf über
              4000 Metern haben, verschwindet alles Eis im schlimmsten Szenario.
              Bei solch einer Klimaentwicklung wäre es nicht nur für die
              Gletscher äusserst ungemütlich, sondern auch für die ganze
              Schweizer Bevölkerung – der Anstieg der Temperaturen in der
              Schweiz läge nämlich deutlich über den 4,4 Grad im weltweiten
              Mittel, was zu einer Vielzahl anderer Probleme wie extreme Hitze,
              Dürre und Starkregen führen wird. Die Reaktion der Gletscher bei
              einer Einhaltung des 1,5-°C-Ziels ist sehr verschieden: Während
              einzelne Gletscher (Unteraar, Plaine Morte, Forno) bis 2100
              trotzdem fast ganz verschwinden, schaffen es andere, über die
              Hälfte ihres aktuellen Volumens zu behalten (Gorner, Fiescher,
              Allalin).
            </div>
          </div>
          <div className='ui ta text teaser container'>
            <div className='column-wrapper'>
              <div className='person'>
                <img
                  className='ui medium circular image left'
                  src={require('../../images/matthias-huss.jpg')}
                />
              </div>
              <div className='quote'>
                «Noch nie standen die Gletscher Ende Juni schlechter da»
              </div>
            </div>
            <div className='call'>
              Zum{' '}
              <Link href='632702684498' hideIcon allowWordWrap>
                Interview mit ETH-Glaziologe
              </Link>{' '}
              Matthias Huss.
            </div>
          </div>
        </div>

        <hr className='divider' />

        <Methodology />

        <Footer
          inverted
          hidePortfolioTeaser
          articleId={ARTICLE_ID}
          questions={[
            'Wie informativ war der Beitrag?',
            'Wie verständlich sind die Visualisierungen?'
          ]}
          credits={[
            [
              'Gletscher-Modellierung, Expertise, Text',
              '<nobr>Matthias Huss</nobr>, Glaziologe, ETH Zürich/WSL Birmensdorf, Leiter Schweizer Gletschermessnetz <a href="https://www.glamos.ch">GLAMOS</a>'
            ],
            [
              'Gletscher-3D-Visualisierung',
              '<nobr>Enrico Mattea</nobr>, Universität Freiburg, Departement für Geowissenschaften'
            ],
            [
              'Konzept und Gestaltung',
              '<nobr>Mathias Lutz</nobr>, <nobr>Marc Brupbacher</nobr>'
            ]
          ]}
        />
      </div>
    )
  }
}
